import { type ComponentProps, type FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useRouteLoaderData } from '@remix-run/react';
import { useComposerScheduling } from '@shared/dream-components';
import { useQueryClient } from '@tanstack/react-query';

import useIsMobile from '~/hooks/useIsMobile';
import useToast, { type ToastData, ToastStatus } from '~/hooks/useToast';
import type { CommunityScheduledPost } from '~/interfaces/community';
import type { loader as communityLoader } from '~/routes/community';

import { useCommunityMembership } from '../../_hooks/useCommunityMembership';
import useCommunityScheduledPosts, { SCHEDULED_POSTS_QUERY_KEY } from '../../_hooks/useCommunityScheduledPosts';
import useCreateCommunityScheduledPostMutation from '../../_hooks/useCreateCommunityScheduledPostMutation';
import useUpdateCommunityScheduledPostMutation from '../../_hooks/useUpdateCommunityScheduledPostMutation';
import { type ComposerInitialDraft, PostComposerModal, type PostComposerScheduling } from './PostComposerModal';
import { ScheduledQueuePanel } from './ScheduledQueuePanel';
import { SchedulePostPanel } from './SchedulePostPanel';

type PostComposerSchedulerModalProps = Omit<ComponentProps<typeof PostComposerModal>, 'initialDraft' | 'scheduling'>;

interface ComposerWithSchedulingProps extends PostComposerSchedulerModalProps {
  editScheduledPost?: CommunityScheduledPost;
  initialPanel?: 'queue';
  onEditScheduledPost: (post: CommunityScheduledPost) => void;
  onScheduledPostSaved: () => void;
}

// The composer as the community opens it, scheduling included. A post picked in the queue opens in a fresh composer,
// since the post sets the draft and the publish time the composer starts with. Saving it leaves a fresh composer with
// the queue open, which shows the post as saved.
export const PostComposerSchedulerModal: FC<PostComposerSchedulerModalProps> = ({ quotedPost, ...props }) => {
  // The mountKey changes on every Edit from the queue, even for the same post, and on every save, so the composer
  // remounts on its draft.
  const [mount, setMount] = useState<{ post?: CommunityScheduledPost; initialPanel?: 'queue'; mountKey: number }>({
    mountKey: 0,
  });

  return (
    <ComposerWithScheduling
      key={mount.mountKey}
      {...props}
      // The quote belongs to the composer as it first opened, whose draft a post from the queue replaced.
      quotedPost={mount.mountKey === 0 ? quotedPost : undefined}
      disableInitialTransition={mount.mountKey > 0}
      editScheduledPost={mount.post}
      initialPanel={mount.initialPanel}
      onEditScheduledPost={(post) => setMount(({ mountKey }) => ({ post, mountKey: mountKey + 1 }))}
      onScheduledPostSaved={() => setMount(({ mountKey }) => ({ initialPanel: 'queue', mountKey: mountKey + 1 }))}
    />
  );
};

// What the composer schedules with: the team's queue, the publish time, the picker and the queue that take turns over
// the hidden composer, and where a scheduled post is sent. The composer keeps the draft and sends a post right away.
const ComposerWithScheduling: FC<ComposerWithSchedulingProps> = ({
  editScheduledPost,
  initialPanel,
  onEditScheduledPost,
  onScheduledPostSaved,
  ...props
}) => {
  const { isOpen, editPost, onClose } = props;
  const { t, i18n } = useTranslation();
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();
  const communityLayoutData = useRouteLoaderData<typeof communityLoader>('routes/community');
  const { currentMember, permissions } = useCommunityMembership();
  const canSchedule = !editPost && Boolean(communityLayoutData?.currentMember?.can_schedule_posts);
  const scheduledPostsQuery = useCommunityScheduledPosts({ enabled: isOpen && canSchedule });
  const createScheduledPostMutation = useCreateCommunityScheduledPostMutation();
  const updateScheduledPostMutation = useUpdateCommunityScheduledPostMutation();
  // A post that couldn't publish has a time that has passed, so its edit asks for a new one right away.
  const [error, setError] = useState<string | null>(() =>
    editScheduledPost && new Date(editScheduledPost.scheduled_at) <= new Date()
      ? t('community.composer.schedule.error_passed')
      : null,
  );
  const [toastData, setToastData] = useState<ToastData>();
  const [isRefused, setIsRefused] = useState(false);

  useToast(toastData);

  const { scheduledAt, isTimeChanged, scheduling, panel, pickerFor, queue, hasTimePassed } = useComposerScheduling({
    canSchedule,
    queuedCount: scheduledPostsQuery.data?.scheduled_posts.length,
    nextUsualTime: scheduledPostsQuery.data?.next_usual_time,
    editedScheduledAt: editScheduledPost ? new Date(editScheduledPost.scheduled_at) : undefined,
    locale: i18n.language,
    translate: (key, values) => t(key, values),
    initialPanel,
    onChange: () => setError(null),
  });

  const showError = () => setError(t('community.composer.error'));
  // The picker stays open with the time picked, so it shows a refusal in a toast.
  const refuseSchedule = (message: string) => {
    setIsRefused(true);
    setToastData({ status: ToastStatus.ERROR, message });
  };

  const composerScheduling: PostComposerScheduling = {
    composer: scheduling,
    // The picker and the queue hide the composer instead of closing its dialog.
    isHidden: panel !== 'composer',
    isPending: createScheduledPostMutation.isPending || updateScheduledPostMutation.isPending,
    error,
    isRefused,
    // The chip stays, so the member can pick a new time.
    validate: () => {
      const hasPassed = hasTimePassed();
      setError(hasPassed ? t('community.composer.schedule.error_passed') : null);
      return !hasPassed;
    },
    // A new scheduled post closes the composer and an edited one goes back to the queue. Neither enters the feed caches
    // before it publishes.
    submit: ({ payload, destination, scheduledAt: pickedAt }) => {
      setIsRefused(false);
      if (editScheduledPost) {
        if (!scheduledAt) return true;
        // The edit sends only a removal, since a kept attachment stays as it is and one that is gone opens as removed.
        const dropsAttachment = !payload.attachable_id && Boolean(editScheduledPost.attachable_id);

        updateScheduledPostMutation.mutate(
          {
            scheduled_post_id: editScheduledPost.id,
            title: payload.title,
            content_json: payload.content_json,
            scheduled_at: scheduledAt.toISOString(),
            destination_kind: destination.kind,
            ...(destination.kind === 'channel' ? { destination_space_slug: destination.space.slug } : {}),
            ...(dropsAttachment ? { remove_attachable: 'true' } : {}),
          },
          {
            onSuccess: (data) => {
              if (!('scheduled_post' in data)) {
                setError(data.toast.message ?? t('community.composer.error'));
                return;
              }
              onScheduledPostSaved();
            },
            onError: showError,
            // A refusal reloads the queue too: the post may have started publishing after the queue loaded.
            onSettled: () => queryClient.invalidateQueries({ queryKey: SCHEDULED_POSTS_QUERY_KEY }),
          },
        );
        return true;
      }

      // Without a time from the picker, a new post goes out now, through Post.
      if (!pickedAt) return false;
      createScheduledPostMutation.mutate(
        { ...payload, scheduled_at: pickedAt.toISOString() },
        {
          onSuccess: (data) => {
            if (!('scheduled_post' in data)) {
              refuseSchedule(data.toast.message ?? t('community.composer.error'));
              return;
            }
            queryClient.invalidateQueries({ queryKey: SCHEDULED_POSTS_QUERY_KEY });
            setToastData({ status: ToastStatus.SUCCESS, message: t('community.composer.schedule.toast') });
            onClose();
          },
          onError: () => refuseSchedule(t('community.composer.error')),
        },
      );
      return true;
    },
    renderDialogs: ({ hasDraft, schedule, isSubmitting }) => (
      <>
        {/* The panels mount on every open, so a useIsMobile of their own would start as desktop. */}
        {panel === 'picker' && (
          <SchedulePostPanel {...pickerFor(schedule)} isSubmitting={isSubmitting} autoFocus={!isMobile} />
        )}
        {panel === 'queue' && (
          <ScheduledQueuePanel
            posts={scheduledPostsQuery.data?.scheduled_posts}
            currentMemberId={currentMember?.id}
            isAdmin={permissions.isAdmin}
            // A new time counts as a change to the opened post.
            hasDraft={() => hasDraft() || isTimeChanged}
            onEdit={onEditScheduledPost}
            autoFocus={!isMobile}
            {...queue}
          />
        )}
      </>
    ),
  };

  const initialDraft = useMemo<ComposerInitialDraft | undefined>(
    () =>
      editScheduledPost && {
        title: editScheduledPost.title,
        tiptap_state: editScheduledPost.tiptap_state,
        attachable: editScheduledPost.attachable,
        author: editScheduledPost.author,
        space_slug: editScheduledPost.channel?.slug ?? null,
      },
    [editScheduledPost],
  );

  return <PostComposerModal {...props} initialDraft={initialDraft} scheduling={composerScheduling} />;
};
